import { TextLayer } from '@deck.gl/layers'
import { WebMercatorViewport } from '@deck.gl/core'
import layerService from '@/layers/layerService'
import { ASSETS_IDS_LAYER } from '@/layers/constant'
import type { AssetDataGeoJsonLayer } from '@/plan/filter/asset.type'

// Asset feature plus the computed, decluttered label written onto its properties.
type AssetIdFeature = AssetDataGeoJsonLayer & {
  properties: AssetDataGeoJsonLayer['properties'] & { layerTag?: string }
}

type ViewState = NonNullable<
  ConstructorParameters<typeof WebMercatorViewport>[0]
> & {
  zoom: number
}

interface ViewportBounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

function getViewportBounds(viewState: ViewState): ViewportBounds {
  const viewport = new WebMercatorViewport(viewState)
  const topLeft = viewport.unproject([0, 0])
  const bottomRight = viewport.unproject([viewport.width, viewport.height])

  return {
    minX: topLeft[0],
    maxX: bottomRight[0],
    minY: bottomRight[1],
    maxY: topLeft[1],
  }
}

function filterDataInViewport(
  data: AssetIdFeature[],
  viewState: ViewState,
): AssetIdFeature[] {
  const bounds = getViewportBounds(viewState)

  return data.filter((d) => {
    const [x, y] = d.geometry.coordinates
    return (
      x >= bounds.minX &&
      x <= bounds.maxX &&
      y >= bounds.minY &&
      y <= bounds.maxY
    )
  })
}

// Beyond this zoom the grid cell shrinks below the on-screen footprint of a
// label, so assets a fraction of a meter apart fall into separate cells and
// their labels render on top of each other. Freezing the zoom used for cell
// sizing keeps the cell large enough to keep grouping collided labels into
// `id(+N)` when the user zooms past the fit cap (see layerService FLY_TO_MAX_ZOOM).
// (deck.gl 9 migration, sc-552667)
const MAX_ZOOM_FOR_CELL_SIZE = 17

function calculateCellSize(
  zoomLevel: number,
  zoomLayerForDeclustering: number,
): number {
  // Definir un tamaño base para la celda
  const baseCellSize = 0.025 // Tamaño en unidades de coordenadas del mapa

  // Ajustar el tamaño de la celda basado en el zoom
  // Aumenta el tamaño de la celda a medida que disminuye el zoom
  // y lo reduce a medida que aumenta el zoom.
  const cappedZoomLevel = Math.min(zoomLevel, MAX_ZOOM_FOR_CELL_SIZE)
  const finalZoomLevel = cappedZoomLevel - zoomLayerForDeclustering
  const den = Math.pow(2, finalZoomLevel)
  const cellSize = baseCellSize / den
  return cellSize
}

function createGrid(
  data: AssetIdFeature[],
  zoomLevel: number,
  zoomLayerForDeclustering: number,
): Record<string, AssetIdFeature[]> {
  const cellSize = calculateCellSize(zoomLevel, zoomLayerForDeclustering)
  const grid: Record<string, AssetIdFeature[]> = {}

  data.forEach((d) => {
    const [x, y] = d.geometry.coordinates
    const gridX = Math.floor(x / cellSize)
    const gridY = Math.floor(y / cellSize)
    const key = `${gridX}-${gridY}`

    if (!grid[key]) {
      grid[key] = []
    }

    grid[key].push(d)
  })

  return grid
}

function formatLayerTagForOneId(ids: string[]): string {
  const idsCount = ids.length - 1
  let formattedTag = `${ids[0]}`
  if (idsCount > 0) {
    formattedTag += `(+${idsCount})`
  }
  return formattedTag
}

function formatLayerTag(
  ids: string[],
  elementsPerLine: number,
  maxLines: number,
): string {
  let formattedTag = ''
  let lineCount = 0
  const idsCount = ids.length

  for (let index = 0; index < idsCount; index++) {
    if (index > 0 && index % elementsPerLine === 0) {
      formattedTag += '\n'
      lineCount++
    }
    if (lineCount >= maxLines) {
      formattedTag += '...+' + (idsCount - index)
      break
    }
    formattedTag += ids[index]
    if (index < idsCount - 1) {
      formattedTag += ','
    }
  }

  return formattedTag
}

function filterData(
  grid: Record<string, AssetIdFeature[]>,
  zoomLevel: number,
  showAllAssetsIds: boolean,
): AssetIdFeature[] {
  const filteredData: AssetIdFeature[] = []

  Object.keys(grid).forEach((key) => {
    const cellData = grid[key]

    if (cellData.length > 0) {
      const asset = cellData[cellData.length - 1]
      if (zoomLevel < 17.5) {
        if (showAllAssetsIds) {
          const ids = cellData
            .reverse()
            .map((d) => d.properties.internal_panel_id)
          asset.properties.layerTag = formatLayerTagForOneId(ids)
        } else {
          asset.properties.layerTag = `${asset.properties.internal_panel_id}`
        }
      } else {
        const ids = cellData
          .reverse()
          .map((d) => d.properties.internal_panel_id)
        asset.properties.layerTag = formatLayerTag(ids, 3, 3)
      }
      filteredData.push(asset)
    }
  })

  return filteredData
}

const textLayerProps = {
  id: ASSETS_IDS_LAYER,
  layerType: TextLayer,
  getPosition: (d: AssetIdFeature) => d.geometry.coordinates,
  getText: (d: AssetIdFeature) => d.properties.layerTag,
  getSize: 10,
  getPixelOffset: [0, 15],
  getTextAnchor: 'middle',
  getAlignmentBaseline: 'top',
  background: true,
  backgroundPadding: [5, 5, 5, 5],
  getBackgroundColor: [255, 255, 255, 50],
  getBorderColor: [255, 255, 255, 100],
  getBorderWidth: 2,
}

interface UpdateAssetIdLayerParams {
  viewState: ViewState
  zoomLayerForDeclustering: number
  assets?: AssetIdFeature[]
}

export function updateAssetIdLayer(params: UpdateAssetIdLayerParams): void {
  const { viewState, zoomLayerForDeclustering, assets } = params
  if (assets !== undefined && !Array.isArray(assets)) {
    throw new Error('If paramater assets is provided must be an array')
  }
  console.time('updateAssetIdLayer')
  const currentZoom = viewState.zoom
  if (currentZoom < zoomLayerForDeclustering) {
    layerService.removeLayer(ASSETS_IDS_LAYER)
    return
  }
  const allAssets = assets ?? layerService.getAssetsPaintendOnMap()
  const visibleData = filterDataInViewport(allAssets, viewState)
  console.debug(
    `Plan assets: ${allAssets.length} VS Visible assets: ${visibleData.length}`,
  )
  const grid = createGrid(visibleData, currentZoom, zoomLayerForDeclustering)
  const filteredData = filterData(grid, currentZoom, true)
  console.debug(
    `Visible assets after resolving collisions: ${filteredData.length}`,
  )

  const layerProps = {
    ...textLayerProps,
    data: filteredData,
    updateTriggers: {
      data: viewState.zoom,
    },
  }
  layerService.addLayer(layerProps)

  console.timeEnd('updateAssetIdLayer')
}
